/**
 * Others Tool
 *
 * For backing up miscellaneous Firestore collections
 * not associated with any specific tool.
 * Collections are configurable via the backup page.
 */

import { BaseTool } from "../BaseTool";
import { FaCubes } from "react-icons/fa";

export class OthersTool extends BaseTool {
  constructor() {
    super();
    this.id = "others";
    this.name = "Others";
    this.description = "Miscellaneous collections not tied to specific tools";
    this.icon = FaCubes;
    this.color = "#6B7280";
    // Not a page. This is a backup-scope concept — a bucket for collections no
    // other tool claims — configured inside the Backup tool. Its "path" points
    // at a route that does not exist, so a nav entry for it lands on the
    // dashboard, which is why it must never appear in the sidebar.
    //
    // ⚠️ It only started appearing when admin tools from the registry were
    // added to the System group (so System Health would show up). Anything
    // registered but not navigable needs navHidden, or that change surfaces it.
    this.path = "/dashboard/backup"; // configured within the Backup tool
    this.requiresAdmin = true;
    this.navHidden = true;

    // Collections are loaded from localStorage
    this.collections = this.loadCollections();

    // This tool doesn't have a summary widget
    this.summaryConfig = {
      enabled: false,
    };
  }

  /**
   * Load configured collections from localStorage
   */
  loadCollections() {
    try {
      const stored = localStorage.getItem("others_tool_collections");
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error("Failed to load Others tool collections:", error);
      return [];
    }
  }

  /**
   * Save collections to localStorage
   */
  saveCollections(collections) {
    try {
      localStorage.setItem(
        "others_tool_collections",
        JSON.stringify(collections)
      );
      this.collections = collections;
    } catch (error) {
      console.error("Failed to save Others tool collections:", error);
    }
  }

  /**
   * Validate and clean stored collections
   * Removes any collections that are claimed by other tools or don't exist
   * @param {string[]} validCollections - Array of valid unclaimed collections
   */
  validateCollections(validCollections) {
    const stored = this.loadCollections();
    const validSet = new Set(validCollections);
    const cleaned = stored.filter((col) => validSet.has(col));

    if (cleaned.length !== stored.length) {
      this.saveCollections(cleaned);
    }

    return cleaned;
  }

  /**
   * Get summary data - not implemented since this tool has no dashboard widget
   */
  async getSummaryData() {
    return {
      success: true,
      data: {},
    };
  }

  /**
   * Get collections dynamically (reloads from localStorage)
   */
  getCollections() {
    return this.loadCollections();
  }
}
